import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { buttonVariants } from '@src/design-system/button';
import {
  Pagination as PaginationComponent,
  PaginationItem,
  PaginationContent,
  PaginationLink,
  PaginationEllipsis,
} from '@src/design-system/pagination';
import { cn } from '@src/design-system/helpers';
import { getPaginationItems } from '@shared/utils/pagination-helpers';

export interface PaginationControlProps {
  /**
   * Total number of pages
   */
  totalPages: number;
  /**
   * Current active page (1-indexed)
   */
  currentPage: number;
  /**
   * Callback fired when page changes
   * @param page - The new page number (1-indexed)
   */
  onPageChange: (page: number) => void;
  /**
   * Number of sibling pages to show on each side of current page
   * @default 1
   */
  siblingCount?: number;
  /**
   * Number of boundary pages to show at start and end
   * @default 1
   */
  boundaryCount?: number;
  /**
   * Additional className for the pagination content wrapper
   */
  className?: string;
  /**
   * Whether to show previous/next buttons
   * @default true
   */
  showNavigation?: boolean;
  /**
   * Custom aria label for previous button
   * @default 'Go to previous page'
   */
  previousAriaLabel?: string;
  /**
   * Custom aria label for next button
   * @default 'Go to next page'
   */
  nextAriaLabel?: string;
}

/**
 * Reusable pagination component with ellipses support
 *
 * @example
 * ```tsx
 * <PaginationControl
 *   totalPages={10}
 *   currentPage={5}
 *   onPageChange={(page) => setCurrentPage(page)}
 * />
 * ```
 */
export function PaginationControl({
  totalPages,
  currentPage,
  onPageChange,
  siblingCount = 1,
  boundaryCount = 1,
  className,
  showNavigation = true,
  previousAriaLabel = 'Go to previous page',
  nextAriaLabel = 'Go to next page',
}: PaginationControlProps) {
  // Validate props
  const safeTotalPages = Math.max(0, Math.floor(totalPages));
  const safeCurrentPage = Math.max(
    1,
    Math.min(currentPage, safeTotalPages || 1)
  );

  // Handle edge cases
  if (safeTotalPages <= 0) {
    return null;
  }

  // Get pagination items with ellipses
  const paginationPages = getPaginationItems({
    currentPage: safeCurrentPage,
    totalPages: safeTotalPages,
    siblingCount,
    boundaryCount,
  });

  const handlePrevious = () => {
    if (safeCurrentPage > 1) {
      onPageChange(safeCurrentPage - 1);
    }
  };

  const handleNext = () => {
    if (safeCurrentPage < safeTotalPages) {
      onPageChange(safeCurrentPage + 1);
    }
  };

  const handlePageClick = (page: number) => {
    if (page !== safeCurrentPage && page >= 1 && page <= safeTotalPages) {
      onPageChange(page);
    }
  };

  const canGoPrevious = safeCurrentPage > 1;
  const canGoNext = safeCurrentPage < safeTotalPages;

  return (
    <PaginationComponent>
      <PaginationContent
        className={cn(
          'inline-flex gap-0 bg-white -space-x-px rounded-xl rtl:space-x-reverse max-h-[33px] h-[33px] border border-[#DED6EE] overflow-hidden',
          className
        )}
      >
        {showNavigation && (
          <PaginationItem>
            <PaginationLink
              onClick={handlePrevious}
              aria-label={previousAriaLabel}
              aria-disabled={!canGoPrevious}
              role={canGoPrevious ? 'button' : undefined}
              className={cn(
                buttonVariants({ variant: 'primaryOutline' }),
                'rounded-none shadow-none focus-visible:z-10 aria-disabled:pointer-events-none [&[aria-disabled]>svg]:opacity-50 text-[14px] border-none'
              )}
            >
              <ChevronLeftIcon
                size={14}
                aria-hidden='true'
              />
            </PaginationLink>
          </PaginationItem>
        )}

        {paginationPages.map((item, idx) => {
          if (item === 'LEFT_ELLIPSIS' || item === 'RIGHT_ELLIPSIS') {
            return (
              <PaginationItem key={`${item}-${idx}`}>
                <PaginationEllipsis
                  className={cn(
                    buttonVariants({ variant: 'primaryOutline' }),
                    'pointer-events-none rounded-none shadow-none text-[14px] border-none'
                  )}
                />
              </PaginationItem>
            );
          }

          const isActive = item === safeCurrentPage;

          return (
            <PaginationItem
              key={item}
              className='border border-[#DED6EE]'
            >
              <PaginationLink
                isActive={isActive}
                onClick={() => handlePageClick(item)}
                aria-label={`Go to page ${item}`}
                aria-current={isActive ? 'page' : undefined}
                className={cn(
                  buttonVariants({ variant: 'primaryOutline' }),
                  'rounded-none shadow-none focus-visible:z-10 text-[14px] border-none',
                  isActive && 'bg-accent'
                )}
              >
                {item}
              </PaginationLink>
            </PaginationItem>
          );
        })}

        {showNavigation && (
          <PaginationItem>
            <PaginationLink
              onClick={handleNext}
              aria-label={nextAriaLabel}
              aria-disabled={!canGoNext}
              role={canGoNext ? 'button' : undefined}
              className={cn(
                buttonVariants({ variant: 'primaryOutline' }),
                'rounded-none shadow-none focus-visible:z-10 aria-disabled:pointer-events-none [&[aria-disabled]>svg]:opacity-50 text-[14px] border-none'
              )}
            >
              <ChevronRightIcon
                size={14}
                aria-hidden='true'
              />
            </PaginationLink>
          </PaginationItem>
        )}
      </PaginationContent>
    </PaginationComponent>
  );
}
